import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Clock, Loader2 } from "lucide-react";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { toast } from "sonner";
import { isPushSupported } from "@/lib/pushNotifications";
import { supabase } from "@/integrations/supabase/client";

export default function NotificationSettings() {
  const { preferences, isLoading, isSaving, savePreferences } = useNotificationPreferences();
  const [localEnabled, setLocalEnabled] = useState(preferences.dailyNotificationEnabled);
  const [localTime, setLocalTime] = useState(preferences.dailyNotificationTime);
  const [swStatus, setSwStatus] = useState<string>('Checking...');

  React.useEffect(() => {
    // Check service worker status
    const checkSWStatus = () => {
      if ('serviceWorker' in navigator) {
        if (navigator.serviceWorker.controller) {
          setSwStatus('Controller active');
        } else {
          setSwStatus('No controller');
        }
      } else {
        setSwStatus('Not supported');
      }
    };

    checkSWStatus();

    // Listen for controller changes
    navigator.serviceWorker?.addEventListener('controllerchange', () => {
      console.log('SW controller changed');
      checkSWStatus();
    });
  }, []);

  // Update local state when preferences load
  React.useEffect(() => {
    setLocalEnabled(preferences.dailyNotificationEnabled);
    setLocalTime(preferences.dailyNotificationTime);
  }, [preferences]);

  const handleSave = async () => {
    // Validate time format
    if (localEnabled && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(localTime)) {
      toast.error("Please enter a valid time in HH:MM format");
      return;
    }

    await savePreferences({
      dailyNotificationEnabled: localEnabled,
      dailyNotificationTime: localTime,
    });
  };

  const handleToggle = async (enabled: boolean) => {
    setLocalEnabled(enabled);

    // If disabling, save immediately
    if (!enabled) {
      await savePreferences({
        dailyNotificationEnabled: false,
        dailyNotificationTime: localTime,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Daily Habit Reminder
          </Label>
          <p className="text-sm text-muted-foreground">
            Get a daily notification to help you remember to log your habits
          </p>
        </div>
        <Switch
          checked={localEnabled}
          onCheckedChange={handleToggle}
          disabled={isSaving}
        />
      </div>

      {localEnabled && (
        <div className="space-y-2 pl-6 border-l-2 border-muted">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Reminder Time
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="time"
              value={localTime}
              onChange={(e) => setLocalTime(e.target.value)}
              className="w-32"
              disabled={isSaving}
            />
            <Button
              onClick={handleSave}
              disabled={isSaving}
              size="sm"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            You'll receive a notification at this time every day.
            {isPushSupported ? (
              <span className="text-green-600 font-medium">✓ Push notifications available for background delivery</span>
            ) : (
              <span className="text-orange-600">⚠ Push notifications not supported - using foreground notifications</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            SW Status: {swStatus}
          </p>
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  console.log('Sending test push notification...');
                  const { data: { session } } = await supabase.auth.getSession();

                  if (!session?.access_token) {
                    toast.error('Not authenticated');
                    return;
                  }

                  const response = await fetch('/api/push/send', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                      title: "🔔 Test Push Notification",
                      body: "This tests your background push notification system! If you see this, it's working perfectly.",
                      delay: 10, // Send after 10 seconds
                      data: {
                        type: 'test_notification',
                      }
                    })
                  });

                  if (!response.ok) {
                    const errorText = await response.text();
                    console.error('API error response:', errorText);
                    throw new Error(`API failed with status ${response.status}`);
                  }

                  const result = await response.json();
                  console.log('Test push result:', result);

                  if (result.success) {
                    if (result.delay) {
                      toast.success(`🔔 Test notification scheduled! Will arrive in ${result.delay} seconds.`);
                    } else if (result.results && result.results.successful > 0) {
                      toast.success('🔔 Test notification sent! Close the app completely and check your device.');
                    } else {
                      toast.error('Test notification failed - check server logs');
                    }
                  } else {
                    toast.error('Test notification failed - check server logs');
                  }
                } catch (error) {
                  console.error('Test push notification failed:', error);
                  toast.error(`Test failed: ${error.message}`);
                }
              }}
            >
              Send Test Push
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
